import { useState } from "react"
import { Box, Chip, Divider, ListSubheader, Menu, MenuItem, Tooltip } from "@mui/material"
import { useDraggable } from "@dnd-kit/core"
import { useBoundContext } from "lib/@components/binding/use-bound-context"
import Iconify from "minimals-template/components/Iconify"
import { CloseIcon } from "theme/overrides/CustomIcons"
import { toDataCy } from "lib/to-data-cy"
import { splitTaskAssignmentTargetId } from "routes/skill-mapper/server/schedule-assignment-routing.mjs"
import { If } from "lib/@components/switch"
import { ASSIGNMENT_KIND_NAMES as KIND_NAMES } from "./assignment-kind-names"

const KIND_ICONS = {
    folder: "mdi:folder-outline",
    schedule: "mdi:file-document-outline",
    task: "mdi:checkbox-marked-circle-outline",
}

/**
 * A folder, schedule or task assigned to a trade. Clicking it opens a menu: a folder's
 * schedules (or a schedule's tasks) to open, and "Move to" another trade, so everything a drag
 * does can be done from the keyboard too. Dragging it onto another trade also moves it. Its
 * remove button names what takes over, and the board offers Undo once it is removed. Its
 * tooltip gives the full name and the conditional skill rules the assignment ignores. A folder
 * chip separates its schedules in the regime being viewed from its wider scope; a schedule or
 * task chip from another regime says so, as its tasks cannot be opened here.
 */
export function AssignmentChip({ chip }) {
    const { board, readOnly, onRemoveAssignment, onOpenSchedule, onAssignTargets } = useBoundContext()
    const [menuAnchor, setMenuAnchor] = useState(null)
    // A chip still saving has no server id to remove yet
    const handleDelete = readOnly || chip.pending ? undefined : handleRemove
    // Pointer listeners only: the chip is already a button (open its menu, or delete with
    // Backspace), and dnd-kit's attributes would announce a keyboard drag that does not exist
    const { listeners, setNodeRef, isDragging } = useDraggable({
        id: `chip:${chip.id}`,
        data: { chip },
        disabled: readOnly || chip.pending,
    })
    const cursor = readOnly ? "default" : "grab"
    const regimeCount = chip.schedules.length
    const removalTip = `${chip.removal?.title}. ${chip.removal?.text}`
    // While the pointer is on the remove button, the chip's tooltip names what takes over
    const [overRemove, setOverRemove] = useState(false)
    const tooltip = overRemove ? removalTip : describe(chip)
    const otherTrades = (board?.trades ?? []).filter(isOtherTrade)

    return (
        <>
            {/* Above the chip: below it, a tooltip left open when focus returns from the chip's
                menu covered the trade pane's New trade button */}
            <Tooltip title={tooltip} placement="top">
                <Chip
                    ref={setNodeRef}
                    {...listeners}
                    size="small"
                    variant="outlined"
                    // Neutral whatever it overrides: the rules it ignores are in the tooltip, and the
                    // warning is given where it matters, in the selection bar and the task drawer
                    icon={<Iconify icon={KIND_ICONS[chip.kind]} width={16} />}
                    label={
                        <>
                            {KIND_NAMES[chip.kind]}: {chip.label}
                            <If value={chip.kind === "folder"}>
                                <Box component="span" sx={{ color: "text.secondary" }}>
                                    {" "}
                                    · {regimeCount} {"schedule".pluralize(regimeCount)} in this regime
                                </Box>
                            </If>
                            <If value={!chip.inRegime}>
                                <Box component="span" sx={{ color: "text.secondary" }}>
                                    {" "}
                                    · not in this regime
                                </Box>
                            </If>
                        </>
                    }
                    onClick={openMenu}
                    onDelete={handleDelete}
                    // The theme's default Chip delete icon, passed explicitly to carry the test hook
                    // and to name what takes over once the assignment is removed
                    deleteIcon={
                        <CloseIcon
                            data-cy="assignment-chip-remove"
                            aria-label={removalTip}
                            onMouseEnter={showRemoval}
                            onMouseLeave={hideRemoval}
                        />
                    }
                    data-cy={toDataCy("assignment-chip", chip.kind)}
                    sx={{ maxWidth: 1, cursor, opacity: chipOpacity(chip, isDragging) }}
                />
            </Tooltip>
            <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={closeMenu}>
                {menuItems()}
            </Menu>
        </>
    )

    function isOtherTrade(trade) {
        return trade.tradeId !== chip.tradeId
    }

    function showRemoval() {
        setOverRemove(true)
    }

    function hideRemoval() {
        setOverRemove(false)
    }

    function openMenu(event) {
        setMenuAnchor(event.currentTarget)
    }

    function closeMenu() {
        setMenuAnchor(null)
    }

    // MenuItems stay direct Menu children (an array, not <Repeat>) so MUI wires up each one
    function menuItems() {
        const items = chip.kind === "folder" ? folderScheduleItems() : [openTasksItem()]
        if (readOnly || chip.pending || !otherTrades.length) return items
        return [
            ...items,
            <Divider key="move-divider" />,
            <ListSubheader key="move-header">Move to</ListSubheader>,
            ...otherTrades.map(toMoveItem),
        ]
    }

    function folderScheduleItems() {
        if (!chip.schedules.length) {
            return [
                <MenuItem key="none" disabled>
                    No schedules in this regime
                </MenuItem>,
            ]
        }
        return chip.schedules.map(toScheduleItem)
    }

    function openTasksItem() {
        if (!chip.inRegime) {
            return (
                <MenuItem key="open" disabled>
                    Not in this regime, so its tasks can't be opened here
                </MenuItem>
            )
        }
        return (
            <MenuItem key="open" onClick={openOwnSchedule} data-cy="assignment-chip-open">
                Open tasks
            </MenuItem>
        )
    }

    function toScheduleItem(schedule) {
        return (
            <MenuItem
                key={schedule.scheduleId}
                onClick={openSchedule(schedule.scheduleId)}
                data-cy="folder-chip-schedule"
            >
                {schedule.code} {schedule.title}
            </MenuItem>
        )
    }

    function toMoveItem(trade) {
        return (
            <MenuItem key={`move:${trade.tradeId}`} onClick={moveTo(trade.tradeId)} data-cy="assignment-chip-move-to">
                {trade.tradeName}
            </MenuItem>
        )
    }

    function openSchedule(scheduleId) {
        return function handleOpenSchedule() {
            closeMenu()
            onOpenSchedule(scheduleId)
        }
    }

    function openOwnSchedule() {
        closeMenu()
        onOpenSchedule(scheduleIdOf(chip))
    }

    // The same move a drag onto that trade makes
    function moveTo(tradeId) {
        return function handleMove() {
            closeMenu()
            onAssignTargets(tradeId, [{ kind: chip.kind, targetId: chip.targetId, name: chip.name || chip.label }], {
                moving: true,
            })
        }
    }

    function handleRemove() {
        onRemoveAssignment(chip.id)
    }
}

function scheduleIdOf(chip) {
    return chip.kind === "task" ? splitTaskAssignmentTargetId(chip.targetId).scheduleId : chip.targetId
}

// The full name first, since a long chip label is cut short
function describe(chip) {
    const what = {
        folder: "Includes schedules added later, in every regime using this skill map.",
        schedule: "The whole schedule.",
        task: chip.schedule ? `A single task in ${chip.schedule}.` : "A single task.",
    }[chip.kind]
    const rules = chip.overriddenRules.length
        ? ` Ignores conditional skill rules: ${chip.overriddenRules.join(", ")}.`
        : ""
    return `${KIND_NAMES[chip.kind]}: ${chip.label}. ${what}${rules}`
}

function chipOpacity(chip, isDragging) {
    if (isDragging) return 0.4
    return chip.pending ? 0.6 : 1
}
